import { cva } from 'class-variance-authority';
import Link from 'next/link';
import { memo, useMemo } from 'react';
import { DownloadHistoryIcon } from '@/components/Icons/DownloadHistoryIcon';
import Tooltip from '@/components/Tooltip';
import { Button } from '@music-vine/cadence/ui';
import { useDownloads } from '@/hooks/markers/useDownloads';
import { cn } from '@/lib/utils';
import type { AssetType } from '@/types/asset';
import type { VariantProps } from 'class-variance-authority';
interface DownloadHistoryButtonProps extends VariantProps<typeof downloadHistoryButtonVariants> {
  buttonClassName?: string;
  containerClassName?: string;
  iconClassName?: string;
  id: number;
  name: string;
  type: AssetType;
}
const downloadHistoryButtonVariants = cva('rounded-full', {
  variants: {
    variant: {
      default: 'dark:enabled:hover:bg-gray-800 dark:[&:is(a[href])]:hover:bg-gray-800',
      inverted: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const iconVariants = cva('size-4', {
  variants: {
    variant: {
      default: 'text-gray-950 dark:text-gray-300',
      inverted: 'text-gray-300 dark:text-gray-950'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
export const DownloadHistoryButton = memo<DownloadHistoryButtonProps>(({
  id,
  // For sfx, use sfxVariantId
  name,
  type,
  variant,
  containerClassName,
  buttonClassName,
  iconClassName
}) => {
  const {
    isDownloaded
  } = useDownloads({
    id,
    type
  });

  // Memoize the download URL to prevent recalculation on every render
  const downloadUrl = useMemo(() => {
    if (!isDownloaded) {
      return null;
    }
    const url = new URL('/', 'https://temp');
    url.searchParams.append('name', name);
    if (type === 'music') {
      url.searchParams.append('trackid', String(id));
    } else if (type === 'sfx') {
      url.searchParams.append('sfxid', String(id));
    } else if (type === 'motion') {
      url.searchParams.append('mgid', String(id));
    }
    return `/my/downloads/${url.search}`;
  }, [isDownloaded, name, type, id]);

  // Memoize button class to prevent recalculation
  const buttonClassNames = useMemo(() => cn(downloadHistoryButtonVariants({
    variant
  }), buttonClassName), [variant, buttonClassName]);

  // Memoize icon class to prevent recalculation
  const iconClassNames = useMemo(() => cn(iconVariants({
    variant
  }), iconClassName), [variant, iconClassName]);

  // Early return if not downloaded
  if (!(isDownloaded && downloadUrl)) {
    return null;
  }
  return <Tooltip label="View download history">
        <Link className={containerClassName} href={downloadUrl}>
          <Button aria-label={`Download history for ${name}`} className={buttonClassNames} size="icon" variant="transparent">
            <DownloadHistoryIcon className={iconClassNames} />
          </Button>
        </Link>
      </Tooltip>;
});
DownloadHistoryButton.displayName = 'DownloadHistoryButton';